import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ShieldCheck } from 'lucide-react';
import type { RnUzurkuvanjeQcRelease, RnUzurkuvanjeReleasePayload } from '../types/rnUzurkuvanjeRequests';

interface Props {
  qcRelease?: RnUzurkuvanjeQcRelease;
  canDecide: boolean;
  onRelease: (payload: RnUzurkuvanjeReleasePayload) => Promise<boolean>;
}

const INPUT_CLS = 'h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50';

const choiceClass = (active: boolean) => `rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
  active
    ? 'border-blue-500 border-l-4 bg-blue-50 text-blue-700'
    : 'border-gray-200 bg-white text-gray-600 hover:bg-blue-50/60'
}`;

// QC lab release decision after analysis (separate from the sampling approval).
// Read-only line once decided; otherwise the form for QC.
const RnUzurkuvanjeReleaseRow: React.FC<Props> = ({ qcRelease, canDecide, onRelease }) => {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<'approved' | 'rejected' | ''>('');
  const [inSpec, setInSpec] = useState<boolean | null>(null);
  const [analysisNumber, setAnalysisNumber] = useState('');
  const [comment, setComment] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  if (qcRelease?.decidedAt) {
    const rejected = qcRelease.decision === 'rejected';
    return (
      <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-2 py-1 text-[11px] font-semibold ${rejected ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
        <ShieldCheck size={14} />
        <span>{t(rejected ? 'rnUzurkuvanjeRequests.rejectedLine' : 'rnUzurkuvanjeRequests.releasedLine')}</span>
        <span>· {qcRelease.decidedByName}</span>
        <span>· {moment(qcRelease.decidedAt).format('DD.MM.YYYY HH:mm')}</span>
        <span>· {t('rnUzurkuvanjeRequests.analysisNumber')}: {qcRelease.analysisNumber}</span>
        <span>· {t('rnUzurkuvanjeRequests.inSpec')}: {t(qcRelease.inSpec ? 'rnUzurkuvanjeRequests.yes' : 'rnUzurkuvanjeRequests.no')}</span>
        {qcRelease.comment && <span className="font-normal">· {qcRelease.comment}</span>}
      </div>
    );
  }
  if (!canDecide) return null;

  const missingKey = !decision || inSpec === null
    ? 'releaseNeedsDecision'
    : !analysisNumber.trim()
      ? 'releaseNeedsAnalysis'
      : !pin.trim()
        ? 'releaseNeedsPin'
        : '';
  const submit = async () => {
    if (missingKey || busy || !decision || inSpec === null) return;
    setBusy(true);
    const ok = await onRelease({ decision, analysisNumber: analysisNumber.trim(), inSpec, comment: comment.trim() || undefined, pin: pin.trim() });
    setBusy(false);
    if (ok) setPin('');
  };

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
      <div className="text-[11px] font-semibold text-gray-700">{t('rnUzurkuvanjeRequests.releaseTitle')}</div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setDecision('approved')} className={choiceClass(decision === 'approved')}>
          {t('rnUzurkuvanjeRequests.decisionApproved')}
        </button>
        <button type="button" onClick={() => setDecision('rejected')} className={choiceClass(decision === 'rejected')}>
          {t('rnUzurkuvanjeRequests.decisionRejected')}
        </button>
        <span className="text-[11px] text-gray-500">{t('rnUzurkuvanjeRequests.inSpec')}:</span>
        <button type="button" onClick={() => setInSpec(true)} className={choiceClass(inSpec === true)}>
          {t('rnUzurkuvanjeRequests.yes')}
        </button>
        <button type="button" onClick={() => setInSpec(false)} className={choiceClass(inSpec === false)}>
          {t('rnUzurkuvanjeRequests.no')}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={analysisNumber}
          onChange={(e) => setAnalysisNumber(e.target.value)}
          placeholder={t('rnUzurkuvanjeRequests.analysisNumber') || ''}
          className={`${INPUT_CLS} w-36`}
        />
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('rnUzurkuvanjeRequests.releaseComment') || ''}
          className={`${INPUT_CLS} min-w-[10rem] flex-1`}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder={t('rnUzurkuvanjeRequests.signaturePin') || ''}
          className={`${INPUT_CLS} w-28`}
        />
        <span
          className="inline-flex"
          title={missingKey ? t(`rnUzurkuvanjeRequests.${missingKey}`) : t('rnUzurkuvanjeRequests.releaseTitle')}
        >
          <button
            type="button"
            onClick={submit}
            disabled={!!missingKey || busy}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <ShieldCheck size={14} />
            {t('rnUzurkuvanjeRequests.release')}
          </button>
        </span>
      </div>
    </div>
  );
};

export default RnUzurkuvanjeReleaseRow;
